import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchor,
  ExperienceAnchorOffsetAbsolute,
  ExperienceAnchorType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography
} from '@material-ui/core';
import { TimePicker } from '@material-ui/pickers';
import { assertNever } from 'assert-never';
import { compact } from 'lodash';
import moment, { max } from 'moment-timezone';
import { createContext, useContext, useRef, useState } from 'react';
import { InfoBox } from '../../../../../components/InfoBox';
import { InfoCircle } from '../../../../../components/InfoCircle';
import { RadioPanel } from '../../../../../components/RadioPanel';
import { SwitchWithLabel } from '../../../../../components/SwitchWithLabel';
import { DateTimeField } from '../../../../../components/Time/DateTimeField';
import { SelectBeforeAfter } from '../../../../../components/Time/SelectBeforeAfter';
import { SelectTimeUnit } from '../../../../../components/Time/SelectTimeUnit';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { GridHalfSized } from '../../../../../layout/Grid';
import { Section } from '../../../../../layout/Section';
import { useCurrentUser } from '../../../../../services/currentUser';
import { useFeatureEnabled } from '../../../../../services/features';
import { updateDataInDoc } from '../../../../../services/firebase';
import { lightMdToJsx } from '../../../../../services/formatter';
import {
  fromMoment,
  ONE_DAY,
  printTz,
  toMoment,
  toWeekOrDayDuration
} from '../../../../../services/time';
import { useTheme } from '../../../../../themes';
import { useAnchorFields } from '../../../components/Anchors';
import { Duration } from '../../../components/Builders/TaskBuilder/components/TimeSelectors';
import { UserTimezoneHandlingInfoBox } from '../../../components/UserTimezoneHandlongInfoBox';
import { UserTimezoneHandlingPicker } from '../../../components/UserTimezoneHnadlingPicker';
import { StandardFooter } from '../Footer';
import { StepBodyProps } from '../types';

export type AnchorStash = Partial<Record<ExperienceAnchorType, ExperienceAnchor>>;

/**
 * Last anchor seen for each start-setting type, so switching options and back
 * restores what was there instead of a default. The editor page provides a ref
 * that outlives this step, so the memory survives moving between steps and
 * ends when the editor is left.
 */
export const AnchorStashContext =
  createContext<React.MutableRefObject<AnchorStash> | null>(null);

const updateExpectedEndThreshold = (
  d: Doc<IExperienceTemplate>,
  expectedEndThreshold: number
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, { expectedEndThreshold });
};

export const StepLaunchDatePreview = ({
  d
}: {
  d: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const anchorFields = useAnchorFields(org.customUserFields);
  const { preferUserTimezone } = d.data;
  // count how many participants match
  const { anchor } = d.data;
  if (anchor.type === ExperienceAnchorType.FIXED) {
    return (
      <div>
        <Typography variant="h6">{t('Calendar date')}</Typography>
        <div>
          {t('Starts on')}{' '}
          {toMoment(anchor.ts).tz(d.data.tz).format('dddd, YYYY/MM/DD HH:mm z')}
        </div>
        <Typography variant="caption">
          {t(
            'Participants enrolled after this date will start the following day at {{ date }}',
            {
              date: toMoment(anchor.ts).tz(d.data.tz).format('HH:mm z')
            }
          )}
        </Typography>
      </div>
    );
  }
  if (anchor.type === ExperienceAnchorType.IMMEDIATE) {
    return (
      <div>
        <Typography variant="h6">
          {t('After a participant is enrolled')}
        </Typography>
        {anchor.offset.type === 'ABSOLUTE' && (
          <div>
            {compact(
              [
                t('Starts'),
                anchor.offset.duration === 0 && t('on the day of enrollment'),
                anchor.offset.duration !== 0 &&
                  `${toWeekOrDayDuration(
                    anchor.offset.duration,
                    t
                  )} after enrollment`,
                t('at'),
                moment()
                  .tz(d.data.tz)
                  .hour(anchor.offset.hour)
                  .minutes(anchor.offset.minute || 0)
                  .format(
                    `HH:mm ${
                      preferUserTimezone ? `[${t(`in user's timezone`)}]` : 'z'
                    }`
                  )
              ]
                .filter((t) => t)
                .join(' ')
            )}
          </div>
        )}
      </div>
    );
  }
  if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    return (
      <div>
        <Typography variant="h6">{t('Before or after a milestone')}</Typography>
        {anchor.offset.type === 'ABSOLUTE' && (
          <div>
            {compact([
              t('Starts'),
              anchor.offset.duration === 0 && t('right after'),
              anchor.offset.duration !== 0 &&
                `${toWeekOrDayDuration(anchor.offset.duration, t)} ${
                  anchor.offset.duration > 0 ? t('after') : t('before')
                }`,
              anchorFields.find((a) => a.field === anchor.field)?.label(t),
              t('at'),
              moment()
                .tz(d.data.tz)
                .hour(anchor.offset.hour)
                .minutes(anchor.offset.minute || 0)
                .format(
                  `HH:mm ${
                    preferUserTimezone ? `[${t(`in user's timezone`)}]` : 'z'
                  }`
                )
            ])
              .filter((t) => t)
              .join(' ')}
          </div>
        )}
        <div></div>
      </div>
    );
  }

  return null;
};

const updateAnchor = (
  d: Doc<IExperienceTemplate>,
  nextAnchor: ExperienceAnchor
) => {
  return updateDataInDoc(d, {
    anchor: nextAnchor,
    enrollFuture:
      nextAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
      nextAnchor.isRecurring
        ? false
        : d.data.enrollNewUsers
  });
};

const updatePreferUserTimezone = (
  d: Doc<IExperienceTemplate>,
  nextValue: boolean
) => {
  return updateDataInDoc(d, {
    preferUserTimezone: nextValue
  });
};

const OffsetDuration = ({
  value,
  onChange,
  alwaysAfter
}: {
  value: ExperienceAnchorOffsetAbsolute;
  onChange: (
    nextValue: ExperienceAnchorOffsetAbsolute,
    other: { hasDirectionChanged: boolean }
  ) => void;
  alwaysAfter?: boolean;
}) => {
  console.log(value);
  const days = Math.round(value.duration / ONE_DAY);
  const absDays = Math.abs(days);
  const [unit, setUnit] = useState<'d' | 'w'>(
    days !== 0 && days % 7 === 0 ? 'w' : 'd'
  );
  const [multiplier, setMultiplier] = useState(
    days < 0 && !alwaysAfter ? -1 : 1
  );
  const numberValue = unit === 'w' ? Math.round(absDays / 7) : absDays;
  const theme = useTheme();

  return (
    <>
      <TextField
        value={numberValue}
        type="number"
        inputProps={{
          min: 0
        }}
        style={{ width: theme.spacing(7) }}
        variant="outlined"
        onChange={(ev) => {
          const nextValue = +ev.target.value;
          onChange(
            {
              ...value,
              duration:
                nextValue * ONE_DAY * (unit === 'w' ? 7 : 1) * multiplier
            },
            { hasDirectionChanged: false }
          );
        }}
      />
      <SelectTimeUnit
        value={unit}
        plural={numberValue !== 1}
        onChange={(nextUnit) => {
          setUnit(nextUnit);
          if (nextUnit === 'w') {
            const weeks = Math.max(1, Math.round(absDays / 7));
            onChange(
              {
                ...value,
                duration: weeks * 7 * ONE_DAY * multiplier
              },
              { hasDirectionChanged: false }
            );
          }
        }}
      />
      {!alwaysAfter && (
        <SelectBeforeAfter
          value={multiplier > 0 ? 'aft' : 'bef'}
          onChange={(n) => {
            const nextMultiplier = n === 'aft' ? 1 : -1;
            setMultiplier(nextMultiplier);
            onChange(
              {
                ...value,
                duration: absDays * ONE_DAY * nextMultiplier
              },

              { hasDirectionChanged: true }
            );
          }}
        />
      )}
    </>
  );
};

const InputGrid = styled('div')((p) => ({
  display: 'grid',
  gridColumnGap: p.theme.spacing(),
  alignItems: 'center'
}));

const EmployeeDateInputGrid = styled(InputGrid)((p) => ({
  gridTemplateColumns: 'max-content max-content 1fr 2fr max-content max-content'
}));

const EmployeeDateInputGridV2 = styled(InputGrid)((p) => ({
  gridTemplateColumns:
    'max-content max-content 0.75fr 1.5fr max-content max-content max-content'
}));

const EmployeeDateLaunchDate = ({
  value,
  onChange,
  preferUserTimezone,
  onChangePreferUserTimezone,
  tz,
  customFields,
  minutesStep
}: {
  value: ExperienceAnchor;
  onChange: (nextAnchor: ExperienceAnchor) => void;
  preferUserTimezone: boolean;
  onChangePreferUserTimezone: (nextValue: boolean) => void;
  tz: string;
  customFields: CustomUserField[];
  minutesStep?: number;
}) => {
  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );
  const { t } = useUnsafeTranslation();
  const anchorFields = useAnchorFields(customFields, { realDatesOnly: true });
  const { offset } = value;
  if (value.type !== ExperienceAnchorType.EMPLOYEE_DATE) {
    return null;
  }
  if (offset.type === 'RELATIVE_TO_CALENDAR') {
    return null;
  }

  const GridComponent = userTimezoneHandlingEnabled
    ? EmployeeDateInputGridV2
    : EmployeeDateInputGrid;

  const selectedAnchorField = anchorFields.find((a) => a.field === value.field);

  return (
    <>
      <GridComponent>
        <OffsetDuration
          value={offset}
          onChange={(nextOffset, { hasDirectionChanged }) =>
            onChange({
              ...value,
              offset: nextOffset,
              autoEnrollmentRejectionThreshold:
                hasDirectionChanged &&
                value.autoEnrollmentRejectionThreshold !== null
                  ? value.autoEnrollmentRejectionThreshold * -1
                  : value.autoEnrollmentRejectionThreshold
            })
          }
        />
        <Select
          variant="outlined"
          value={value.field}
          onChange={(ev) => {
            const item = anchorFields.find((a) => a.field === ev.target.value);
            if (!item) {
              return;
            }
            onChange({
              type: ExperienceAnchorType.EMPLOYEE_DATE,
              field: item.field,
              isRecurring: item.isRecurring,
              autoEnrollmentRejectionThreshold:
                value.autoEnrollmentRejectionThreshold,
              offset,
              v: 2
            });
          }}
        >
          {anchorFields.map((a) => (
            <MenuItem key={a.key} value={a.field}>
              {a.label(t)}
            </MenuItem>
          ))}
        </Select>
        <div>{t('at')}</div>
        <TimePicker
          inputVariant="outlined"
          orientation="portrait"
          minutesStep={minutesStep}
          value={moment()
            .hour(offset.hour)
            .minute(offset.minute || 0)}
          onChange={(m) =>
            onChange({
              ...value,
              offset: {
                ...offset,
                hour: m!.hour(),
                minute: m!.minute()
              }
            })
          }
          inputProps={{
            style: { width: 72 }
          }}
          InputProps={{
            endAdornment: userTimezoneHandlingEnabled ? null : (
              <InputAdornment position="end">{printTz(tz)}</InputAdornment>
            )
          }}
        />
        {userTimezoneHandlingEnabled ? (
          <UserTimezoneHandlingPicker
            value={preferUserTimezone}
            onChange={onChangePreferUserTimezone}
            defaultTz={tz}
          />
        ) : null}
      </GridComponent>

      <SwitchWithLabel
        label={t("Reject automatic enrollments if they're too late")}
        checked={value.autoEnrollmentRejectionEnabled ?? true}
        onChange={(nextChecked) => {
          onChange({
            ...value,
            autoEnrollmentRejectionEnabled: nextChecked,
            // Initialize threshold if enabling and currently null
            autoEnrollmentRejectionThreshold:
              nextChecked && value.autoEnrollmentRejectionThreshold === null
                ? offset.duration < 0
                  ? -ONE_DAY * 7
                  : ONE_DAY * 7
                : value.autoEnrollmentRejectionThreshold
          });
        }}
      />

      {(value.autoEnrollmentRejectionEnabled ?? true) &&
        value.autoEnrollmentRejectionThreshold !== null && (
          <>
            <RejectionThresholdSelector
              value={value.autoEnrollmentRejectionThreshold}
              onChange={(nextThreshold) => {
                onChange({
                  ...value,
                  autoEnrollmentRejectionThreshold: nextThreshold
                });
              }}
              fieldLabel={
                selectedAnchorField?.shortLabel(t) || t('the milestone')
              }
            />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              {(value.autoEnrollmentRejectionThreshold || 0) < 0
                ? t(
                    "Enrollments via import or automation {{timePeriod}} or less before the participant's {{dateField}} will be rejected. Manual enrollments will still be possible.",
                    {
                      timePeriod: toWeekOrDayDuration(
                        Math.abs(value.autoEnrollmentRejectionThreshold || 0),
                        t
                      ),
                      dateField:
                        selectedAnchorField?.shortLabel(t) || t('milestone')
                    }
                  )
                : t(
                    "Enrollments via import or automation {{timePeriod}} or more after the participant's {{dateField}} will be rejected. Manual enrollments will still be possible.",
                    {
                      timePeriod: toWeekOrDayDuration(
                        Math.abs(value.autoEnrollmentRejectionThreshold || 0),
                        t
                      ),
                      dateField:
                        selectedAnchorField?.shortLabel(t) || t('milestone')
                    }
                  )}
            </Typography>
          </>
        )}
    </>
  );
};

const RejectionThresholdSelector = ({
  value,
  onChange,
  fieldLabel
}: {
  value: number;
  onChange: (nextValue: number) => void;
  fieldLabel: string;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const absValue = Math.abs(value);
  const days = Math.round(absValue / ONE_DAY);
  const [unit, setUnit] = useState<'d' | 'w'>(
    days !== 0 && days % 7 === 0 ? 'w' : 'd'
  );
  const numberValue = unit === 'w' ? Math.round(days / 7) : days;
  const isBefore = value < 0;

  return (
    <FlexContainer direction="row" style={{ alignItems: 'center' }}>
      <TextField
        value={numberValue}
        type="number"
        inputProps={{ min: 0 }}
        style={{ width: theme.spacing(7) }}
        variant="outlined"
        onChange={(ev) => {
          const nextNumber = +ev.target.value;
          const nextAbsValue = nextNumber * ONE_DAY * (unit === 'w' ? 7 : 1);
          onChange(isBefore ? -nextAbsValue : nextAbsValue);
        }}
      />
      <SelectTimeUnit
        value={unit}
        plural={numberValue !== 1}
        onChange={(nextUnit) => {
          setUnit(nextUnit);
          if (nextUnit === 'w') {
            const weeks = Math.max(1, Math.round(days / 7));
            const weekDays = weeks * 7;
            onChange(isBefore ? -weekDays * ONE_DAY : weekDays * ONE_DAY);
          }
        }}
      />
      <SelectBeforeAfter
        value={isBefore ? 'bef' : 'aft'}
        onChange={(nextDirection) => {
          onChange(nextDirection === 'bef' ? -absValue : absValue);
        }}
      />
      <span>{fieldLabel}</span>
      <InfoCircle
        tooltip={t(
          'Import effects to enroll employees will default to rejected, when the employee would join the experience this late.'
        )}
      />
    </FlexContainer>
  );
};

const MIN_EXPECTED_END_DAYS = 0;
const MAX_EXPECTED_END_DAYS = 3650;

const ExpectedEndThresholdField = ({
  value,
  onChange
}: {
  value: number;
  onChange: (nextValue: number) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const [rawDays, setRawDays] = useState<string>(
    String(Math.round(value / ONE_DAY))
  );
  const numericDays = Number(rawDays);
  const isInvalid =
    rawDays === '' ||
    Number.isNaN(numericDays) ||
    numericDays < MIN_EXPECTED_END_DAYS ||
    numericDays > MAX_EXPECTED_END_DAYS;

  return (
    <TextField
      value={rawDays}
      type="number"
      inputProps={{
        min: MIN_EXPECTED_END_DAYS,
        max: MAX_EXPECTED_END_DAYS
      }}
      error={isInvalid}
      helperText={
        isInvalid
          ? t('Value must be between {{min}} and {{max}} days', {
              min: MIN_EXPECTED_END_DAYS,
              max: MAX_EXPECTED_END_DAYS
            })
          : undefined
      }
      style={{ width: theme.spacing(6) }}
      variant="outlined"
      onChange={(ev) => {
        const next = ev.target.value;
        setRawDays(next);
        const n = Number(next);
        if (
          next !== '' &&
          !Number.isNaN(n) &&
          n >= MIN_EXPECTED_END_DAYS &&
          n <= MAX_EXPECTED_END_DAYS
        ) {
          onChange(n * ONE_DAY);
        }
      }}
      onBlur={() => {
        const clamped = Number.isNaN(numericDays)
          ? MIN_EXPECTED_END_DAYS
          : Math.min(
              MAX_EXPECTED_END_DAYS,
              Math.max(MIN_EXPECTED_END_DAYS, numericDays)
            );
        setRawDays(String(clamped));
        onChange(clamped * ONE_DAY);
      }}
    />
  );
};

const ImmediateInputGrid = styled(InputGrid)((p) => ({
  gridTemplateColumns: 'max-content max-content max-content max-content'
}));

const ImmediateInputGridV2 = styled(InputGrid)((p) => ({
  gridTemplateColumns:
    'max-content max-content max-content max-content max-content'
}));

const ImmediateLaunchDate = ({
  value,
  onChange,
  preferUserTimezone,
  onChangePreferUserTimezone,
  tz,
  minutesStep
}: {
  value: ExperienceAnchor;
  onChange: (nextAnchor: ExperienceAnchor) => void;
  preferUserTimezone: boolean;
  onChangePreferUserTimezone: (nextValue: boolean) => void;
  tz: string;
  minutesStep?: number;
}) => {
  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );
  const { t } = useUnsafeTranslation();
  const { offset } = value;
  if (value.type !== ExperienceAnchorType.IMMEDIATE) {
    return null;
  }
  if (offset.type === 'RELATIVE_TO_CALENDAR') {
    return null;
  }

  const GridComponent = userTimezoneHandlingEnabled
    ? ImmediateInputGridV2
    : ImmediateInputGrid;

  return (
    <GridComponent>
      <OffsetDuration
        value={offset}
        onChange={(nextOffset) =>
          onChange({
            ...value,
            offset: nextOffset
          })
        }
        alwaysAfter
      />
      <div>{t('after enrollment at')}</div>
      <TimePicker
        inputVariant="outlined"
        orientation="portrait"
        value={moment()
          .hour(offset.hour)
          .minute(offset.minute || 0)}
        minutesStep={minutesStep}
        onChange={(m) =>
          onChange({
            ...value,
            offset: {
              ...offset,
              hour: m!.hour(),
              minute: m!.minute()
            }
          })
        }
        inputProps={{
          style: { width: 72 }
        }}
        InputProps={{
          endAdornment: userTimezoneHandlingEnabled ? null : (
            <InputAdornment position="end">{printTz(tz)}</InputAdornment>
          )
        }}
      />
      {userTimezoneHandlingEnabled ? (
        <UserTimezoneHandlingPicker
          value={preferUserTimezone}
          onChange={onChangePreferUserTimezone}
          defaultTz={tz}
        />
      ) : null}
    </GridComponent>
  );
};

export const StepLaunchDate = ({
  d,
  label,
  onChange,
  goTo,
  nextStep,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt
}: StepBodyProps<Doc<IExperienceTemplate>>) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const localAnchorStashRef = useRef<AnchorStash>({});
  const anchorStashRef = useContext(AnchorStashContext) ?? localAnchorStashRef;

  const containerStyle = {
    marginBottom: 16
  };

  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );

  return (
    <>
      <div style={containerStyle}>
        <Grid container justify="space-between">
          <Grid item xs>
            <Typography component="h1" variant="h3">
              {t('Experience start date')}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <Section>
        <GridHalfSized>
          <RadioPanel
            orientation="vertical"
            value={d.data.anchor.type}
            onChange={(type) => {
              const current = d.data.anchor;
              if (type === current.type) return;
              anchorStashRef.current[current.type] = current;
              const stashed = anchorStashRef.current[type];
              if (stashed) {
                onChange((x) => updateAnchor(x, stashed));
                return;
              }
              if (type === ExperienceAnchorType.FIXED) {
                onChange((x) =>
                  updateAnchor(x, {
                    type: ExperienceAnchorType.FIXED,
                    ts: fromMoment(
                      moment()
                        .tz(d.data.tz)
                        .hour(org.defaultExperienceStartHour)
                        .startOf('h')
                    ),
                    offset: {
                      type: 'ABSOLUTE',
                      duration: 0,
                      hour: org.defaultExperienceStartHour
                    },
                    autoEnrollmentRejectionThreshold: ONE_DAY * 14,
                    autoEnrollmentRejectionEnabled: false,
                    v: 2
                  })
                );
                return;
              }
              if (type === ExperienceAnchorType.IMMEDIATE) {
                onChange((x) =>
                  updateAnchor(x, {
                    type: ExperienceAnchorType.IMMEDIATE,
                    offset: {
                      type: 'ABSOLUTE',
                      duration: 0,
                      hour: org.defaultExperienceStartHour
                    },
                    autoEnrollmentRejectionThreshold: null,
                    autoEnrollmentRejectionEnabled: false,
                    v: 2
                  })
                );
                return;
              }
              if (type === ExperienceAnchorType.EMPLOYEE_DATE) {
                onChange((x) =>
                  updateAnchor(x, {
                    type: ExperienceAnchorType.EMPLOYEE_DATE,
                    field: 'hireDate',
                    isRecurring: false,
                    autoEnrollmentRejectionThreshold: -ONE_DAY * 7,
                    autoEnrollmentRejectionEnabled: false,
                    offset: {
                      type: 'ABSOLUTE',
                      duration: -(ONE_DAY * 7),
                      hour: org.defaultExperienceStartHour
                    },
                    v: 2
                  })
                );
                return;
              }

              return assertNever(type);
            }}
            options={compact([
              {
                value: ExperienceAnchorType.FIXED,
                label: t('Calendar date'),
                description: lightMdToJsx(
                  t(
                    'Start an experience on a specific calendar date, e.g. **January 1st 2021 at 9AM**.'
                  )
                ),
                hint: 'Test test',
                body: d.data.anchor.type === ExperienceAnchorType.FIXED && (
                  <FlexContainerVertical spacing={2}>
                    <FlexContainer>
                      <DateTimeField
                        value={d.data.anchor.ts}
                        tz={d.data.tz}
                        minutesStep={10}
                        onChange={(ts) =>
                          onChange((x) =>
                            updateAnchor(x, {
                              type: ExperienceAnchorType.FIXED,
                              ts,
                              offset: {
                                type: 'ABSOLUTE',
                                duration: 0,
                                hour: toMoment(ts).tz(d.data.tz).hour(),
                                minute: toMoment(ts).tz(d.data.tz).minutes()
                              },
                              autoEnrollmentRejectionThreshold:
                                d.data.anchor
                                  .autoEnrollmentRejectionThreshold ??
                                ONE_DAY * 14,
                              autoEnrollmentRejectionEnabled:
                                d.data.anchor.autoEnrollmentRejectionEnabled ??
                                false,
                              v: 2
                            })
                          )
                        }
                        disablePast
                        hideTz={userTimezoneHandlingEnabled}
                      />

                      {userTimezoneHandlingEnabled && (
                        <UserTimezoneHandlingPicker
                          value={d.data.preferUserTimezone}
                          onChange={(nextValue) =>
                            onChange((x) =>
                              updatePreferUserTimezone(x, nextValue)
                            )
                          }
                          defaultTz={d.data.tz}
                        />
                      )}
                    </FlexContainer>
                    {d.data.preferUserTimezone && (
                      <UserTimezoneHandlingInfoBox tz={d.data.tz} />
                    )}
                  </FlexContainerVertical>
                )
              },
              {
                value: ExperienceAnchorType.EMPLOYEE_DATE,
                label: t('Before or after a milestone'),
                description: lightMdToJsx(
                  t(
                    `Start before or after a date in the participant's profile, e.g. **2 weeks before their first day at work**.`
                  )
                ),
                hint: '11111',
                body: d.data.anchor.type ===
                  ExperienceAnchorType.EMPLOYEE_DATE && (
                  <FlexContainerVertical spacing={2}>
                    <EmployeeDateLaunchDate
                      value={d.data.anchor}
                      onChange={(nextAnchor) =>
                        onChange((x) => updateAnchor(x, nextAnchor))
                      }
                      preferUserTimezone={d.data.preferUserTimezone}
                      onChangePreferUserTimezone={(nextValue) =>
                        onChange((x) => updatePreferUserTimezone(x, nextValue))
                      }
                      tz={d.data.tz}
                      customFields={org.customUserFields}
                      minutesStep={10}
                    />
                    {d.data.anchor.isRecurring && (
                      // every year and 2 weeks is currently hardcoded as it's not configurable. Might change in the future
                      <InfoBox fullWidth>
                        {t(
                          'Participants will be automatically enrolled every year, 2 weeks before their experience starts.'
                        )}
                      </InfoBox>
                    )}
                    {d.data.preferUserTimezone && (
                      <UserTimezoneHandlingInfoBox tz={d.data.tz} />
                    )}
                  </FlexContainerVertical>
                )
              },
              {
                value: ExperienceAnchorType.IMMEDIATE,
                label: t('After a participant is enrolled'),
                description: lightMdToJsx(
                  t(
                    'Start after someone is added to the experience, e.g. **2 days after they are enrolled**.'
                  )
                ),
                body: d.data.anchor.type === ExperienceAnchorType.IMMEDIATE && (
                  <FlexContainerVertical spacing={2}>
                    <ImmediateLaunchDate
                      minutesStep={10}
                      value={d.data.anchor}
                      onChange={(nextAnchor) =>
                        onChange((x) => updateAnchor(x, nextAnchor))
                      }
                      preferUserTimezone={d.data.preferUserTimezone}
                      onChangePreferUserTimezone={(nextValue) =>
                        onChange((x) => updatePreferUserTimezone(x, nextValue))
                      }
                      tz={d.data.tz}
                    />
                    {d.data.preferUserTimezone && (
                      <UserTimezoneHandlingInfoBox tz={d.data.tz} />
                    )}
                  </FlexContainerVertical>
                )
              }
            ])}
          />
        </GridHalfSized>
      </Section>
      <div style={containerStyle}>
        <Grid container justify="space-between">
          <Grid item xs>
            <Typography component="h1" variant="h3">
              {t('Experience end date')}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <Section>
        <GridHalfSized>
          <ImmediateInputGrid>
            <div>{t("Archive participants' experiences")}</div>
            <ExpectedEndThresholdField
              value={d.data.expectedEndThreshold}
              onChange={(nextValue) =>
                onChange((x) => updateExpectedEndThreshold(x, nextValue))
              }
            />
            <div>{t('days after the last timeline item')}</div>
          </ImmediateInputGrid>
        </GridHalfSized>
      </Section>
      <StandardFooter d={d} onCheckChanges={onCheckChanges} onLaunchFromRoughDraft={onLaunchFromRoughDraft} isLaunchReady={isLaunchReady} onLaunch={onLaunch} lastSavedAt={lastSavedAt} />
    </>
  );
};
